/**
 * Contrats configurable components (Phase 4 — fiches 22087 / 22089 / 22803
 * / 22805 / 22702 / 22717 / 22719 / 22721 / 22723):
 *
 * | id                                 | component                                    | data props                      |
 * |------------------------------------|----------------------------------------------|---------------------------------|
 * | `extranet.contractsListCard`       | `ContractsListCard` (DataList, endpoint)     | `defaultStatus`                 |
 * | `extranet.contractDetailsCard`     | `EntityInfoCard` + `CONTRACT_DETAILS_FIELDS` | `values`, `loading`, `error`    |
 * | `extranet.contractInsuredCard`     | `EntityInfoCard` + `CONTRACT_INSURED_FIELDS` | `values`, `loading`, `error`    |
 * | `extranet.contractPremiumsCard`    | `ContractPremiumsTableCard` (DataList)       | `rows`, `loading`, `error`      |
 * | `extranet.contractGuaranteesCard`  | `GuaranteesTableCard` (DataList)             | `rows`, `loading`, `error`      |
 * | `extranet.contractClaimsCard`      | `ClaimsTableCard` (DataList)                 | `rows`, `loading`, `error`      |
 * | `extranet.contractDocumentsCard`   | `DocumentsByCategoryCard`                    | `documents`, `loading`, `error` |
 * | `extranet.contractPiecesCard`      | `PiecesCard` + upload (22975)                | `pieces`, `loading`, `error`    |
 *
 * Shared cards are bound per usage with `bindCardProps` (id + config), each
 * usage is registered with `registerOnce` and its data schema, and exported
 * wrapped with `withComponentRules(…, id)` as `<Name>Configurable` — the
 * pages import those, aliased to the plain name.
 */

import { withComponentRules } from '@components/shared/configurable';

import { EXTRANET_MODULE, registerOnce } from '../registerHelpers';
import { bindCardProps } from '../shared/bindCardProps';
import { ClaimsTableCard } from '../shared/ClaimsTableCard';
import { DocumentsByCategoryCard } from '../shared/DocumentsByCategoryCard';
import { EntityInfoCard } from '../shared/EntityInfoCard';
import { GuaranteesTableCard } from '../shared/GuaranteesTableCard';
import { PiecesCard } from '../shared/PiecesCard';
import { PIECE_UPLOAD_CONFIG } from '../shared/pieceUploadConfig';
import {
  CLAIMS_TABLE_PREVIEW_PROPS,
  DOCUMENTS_PREVIEW_PROPS,
  GUARANTEES_TABLE_PREVIEW_PROPS,
  SHARED_CARD_PROP_LABELS,
  claimsTableCardDataSchema,
  documentsCardDataSchema,
  entityInfoCardDataSchema,
  guaranteesTableCardDataSchema,
  piecesCardDataSchema,
  piecesUploadPreviewProps,
  type ClaimsTableCardDataProps,
  type DocumentsCardDataProps,
  type EntityInfoCardDataProps,
  type GuaranteesTableCardDataProps,
  type PiecesCardDataProps,
} from '../shared/schemas';
import { contractDetailContextSchema, contractsListContextSchema } from './contractContext';
import { CONTRACT_DETAILS_FIELDS, CONTRACT_INSURED_FIELDS } from './contractInfoFields';
import { ContractPremiumsTableCard } from './ContractPremiumsTableCard';
import {
  CONTRACTS_LIST_PREVIEW_PROPS,
  CONTRACTS_LIST_PROP_LABELS,
  CONTRACT_DETAILS_PREVIEW_PROPS,
  CONTRACT_INSURED_PREVIEW_PROPS,
  CONTRACT_PREMIUMS_PREVIEW_PROPS,
  contractPremiumsCardDataSchema,
  contractsListCardSchema,
  type ContractPremiumsCardDataProps,
  type ContractsListCardDataProps,
} from './contractSchemas';
import { ContractsListCard } from './ContractsListCard';
import { CONTRACTS_LIST_CARD_ID } from './contractsListConfig';

/** Registered ids of the Contrats components. */
export const CONTRACT_COMPONENT_IDS = {
  list: CONTRACTS_LIST_CARD_ID,
  details: 'extranet.contractDetailsCard',
  insured: 'extranet.contractInsuredCard',
  premiums: 'extranet.contractPremiumsCard',
  guarantees: 'extranet.contractGuaranteesCard',
  claims: 'extranet.contractClaimsCard',
  documents: 'extranet.contractDocumentsCard',
  pieces: 'extranet.contractPiecesCard',
} as const;

const IDS = CONTRACT_COMPONENT_IDS;

// ---------------------------------------------------------------------------
// Bound usages of the shared cards (config props fixed per usage)
// ---------------------------------------------------------------------------

export const ContractDetailsCard = bindCardProps(EntityInfoCard, {
  componentId: IDS.details,
  fields: CONTRACT_DETAILS_FIELDS,
});

export const ContractInsuredCard = bindCardProps(EntityInfoCard, {
  componentId: IDS.insured,
  fields: CONTRACT_INSURED_FIELDS,
});

export const ContractPremiumsCard = bindCardProps(ContractPremiumsTableCard, {
  componentId: IDS.premiums,
});

export const ContractGuaranteesCard = bindCardProps(GuaranteesTableCard, {
  componentId: IDS.guarantees,
});

export const ContractClaimsCard = bindCardProps(ClaimsTableCard, {
  componentId: IDS.claims,
});

export const ContractDocumentsCard = bindCardProps(DocumentsByCategoryCard, {
  componentId: IDS.documents,
});

/** Pièces tab: piece upload on (fiche 22975 — `depot` zone on each pending piece). */
export const ContractPiecesCard = bindCardProps(PiecesCard, {
  componentId: IDS.pieces,
  upload: PIECE_UPLOAD_CONFIG,
});

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

const STATE_LABELS = {
  loading: SHARED_CARD_PROP_LABELS.loading,
  error: SHARED_CARD_PROP_LABELS.error,
} as const;

/** Register the Contrats components. Idempotent. */
export function registerContractComponents(): void {
  registerOnce<ContractsListCardDataProps>({
    id: IDS.list,
    displayName: 'Liste des contrats',
    module: EXTRANET_MODULE,
    component: ContractsListCard,
    propsSchema: contractsListCardSchema,
    defaultPropLabels: CONTRACTS_LIST_PROP_LABELS,
    previewProps: CONTRACTS_LIST_PREVIEW_PROPS,
    contextVariables: contractsListContextSchema,
  });

  registerOnce<EntityInfoCardDataProps>({
    id: IDS.details,
    displayName: 'Détails du contrat',
    module: EXTRANET_MODULE,
    component: ContractDetailsCard,
    propsSchema: entityInfoCardDataSchema,
    defaultPropLabels: { values: SHARED_CARD_PROP_LABELS.values, ...STATE_LABELS },
    previewProps: CONTRACT_DETAILS_PREVIEW_PROPS,
    contextVariables: contractDetailContextSchema,
  });

  registerOnce<EntityInfoCardDataProps>({
    id: IDS.insured,
    displayName: 'Assuré du contrat',
    module: EXTRANET_MODULE,
    component: ContractInsuredCard,
    propsSchema: entityInfoCardDataSchema,
    defaultPropLabels: { values: SHARED_CARD_PROP_LABELS.values, ...STATE_LABELS },
    previewProps: CONTRACT_INSURED_PREVIEW_PROPS,
    contextVariables: contractDetailContextSchema,
  });

  registerOnce<ContractPremiumsCardDataProps>({
    id: IDS.premiums,
    displayName: 'Primes du contrat',
    module: EXTRANET_MODULE,
    component: ContractPremiumsCard,
    propsSchema: contractPremiumsCardDataSchema,
    defaultPropLabels: { rows: SHARED_CARD_PROP_LABELS.rows, ...STATE_LABELS },
    previewProps: CONTRACT_PREMIUMS_PREVIEW_PROPS,
    contextVariables: contractDetailContextSchema,
  });

  registerOnce<GuaranteesTableCardDataProps>({
    id: IDS.guarantees,
    displayName: 'Garanties du contrat',
    module: EXTRANET_MODULE,
    component: ContractGuaranteesCard,
    propsSchema: guaranteesTableCardDataSchema,
    defaultPropLabels: { rows: SHARED_CARD_PROP_LABELS.rows, ...STATE_LABELS },
    previewProps: GUARANTEES_TABLE_PREVIEW_PROPS,
    contextVariables: contractDetailContextSchema,
  });

  registerOnce<ClaimsTableCardDataProps>({
    id: IDS.claims,
    displayName: 'Sinistres du contrat',
    module: EXTRANET_MODULE,
    component: ContractClaimsCard,
    propsSchema: claimsTableCardDataSchema,
    defaultPropLabels: { rows: SHARED_CARD_PROP_LABELS.rows, ...STATE_LABELS },
    previewProps: CLAIMS_TABLE_PREVIEW_PROPS,
    contextVariables: contractDetailContextSchema,
  });

  registerOnce<DocumentsCardDataProps>({
    id: IDS.documents,
    displayName: 'Documents du contrat',
    module: EXTRANET_MODULE,
    component: ContractDocumentsCard,
    propsSchema: documentsCardDataSchema,
    defaultPropLabels: { documents: SHARED_CARD_PROP_LABELS.documents, ...STATE_LABELS },
    previewProps: DOCUMENTS_PREVIEW_PROPS,
    contextVariables: contractDetailContextSchema,
  });

  registerOnce<PiecesCardDataProps>({
    id: IDS.pieces,
    displayName: 'Pièces du contrat',
    module: EXTRANET_MODULE,
    component: ContractPiecesCard,
    propsSchema: piecesCardDataSchema,
    defaultPropLabels: { pieces: SHARED_CARD_PROP_LABELS.pieces, ...STATE_LABELS },
    previewProps: piecesUploadPreviewProps('contrat'),
    contextVariables: contractDetailContextSchema,
  });
}

// ---------------------------------------------------------------------------
// Rule-aware wrappers (imported by the pages)
// ---------------------------------------------------------------------------

export const ContractsListCardConfigurable = withComponentRules(ContractsListCard, IDS.list);
export const ContractDetailsCardConfigurable = withComponentRules(ContractDetailsCard, IDS.details);
export const ContractInsuredCardConfigurable = withComponentRules(ContractInsuredCard, IDS.insured);
export const ContractPremiumsCardConfigurable = withComponentRules(
  ContractPremiumsCard,
  IDS.premiums,
);
export const ContractGuaranteesCardConfigurable = withComponentRules(
  ContractGuaranteesCard,
  IDS.guarantees,
);
export const ContractClaimsCardConfigurable = withComponentRules(ContractClaimsCard, IDS.claims);
export const ContractDocumentsCardConfigurable = withComponentRules(
  ContractDocumentsCard,
  IDS.documents,
);
export const ContractPiecesCardConfigurable = withComponentRules(ContractPiecesCard, IDS.pieces);
